import React, { useContext, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { merge } from 'lodash';

import WDT_CLASSROOM_PROGRESS_BY_STUDENTS_QUERY from 'graph/reports/wdt-classroom-progress-by-students-report-query';
import useReportQueryParams from '../../../../hooks/use-report-query-params';
import BarChart from 'components/@common/Charts/BarChart';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import { ModuleType, ReportEntityType } from '../../../../types';
import { getQueryVariables } from '../../../../utils/report-param-utils';
import { PrintViewContext } from '../../../../providers/PrintViewProvider';
import { WdtClassroomProgressByStudentsData } from 'types/schema-generated-types';
import { sortAlphabeticallyByKeys } from 'utilities/sort-util';
import {
  BAR_CHART_COMMON_OPTIONS,
  MODULE_LENGTH_MAP,
  MODULE_LEVELS_COLOR_MAP
} from '../../../../utils/report-wdt-utils';

interface ProgressForStudentsChartViewProps {
  module: ModuleType;
}

const ProgressForStudentsChartView: React.FC<ProgressForStudentsChartViewProps> = ({ module }) => {
  const { fullChartWidth } = useContext(PrintViewContext);
  const [{ dateRange, entityType, entityId }, setQuery] = useReportQueryParams();
  const [fetchReport, { data, loading }] = useLazyQuery(WDT_CLASSROOM_PROGRESS_BY_STUDENTS_QUERY);
  const { t } = useTranslation('Reports');

  const reportData: WdtClassroomProgressByStudentsData[] =
    data?.wdtClassroomProgressByStudentsReport?.studentProgress ?? [];

  const sortByStudent = (
    a: WdtClassroomProgressByStudentsData,
    b: WdtClassroomProgressByStudentsData
  ): number => sortAlphabeticallyByKeys(['student.firstName', 'student.lastName'], a, b);

  const { ids, students, level1Series, level2Series, level3Series } = reportData
    .slice()
    .sort(sortByStudent)
    .reduce(
      (acc, { student, level, charactersCompletedCount }) => {
        if (!student) return acc;

        acc.ids.add(student.id);
        acc.students.add(`${student.firstName} ${student.lastName}`);

        if (level === 1) acc.level1Series.push(charactersCompletedCount ?? 0);
        if (level === 2) acc.level2Series.push(charactersCompletedCount ?? 0);
        if (level === 3) acc.level3Series.push(charactersCompletedCount ?? 0);

        return acc;
      },
      {
        ids: new Set(),
        students: new Set(),
        level1Series: [] as number[],
        level2Series: [] as number[],
        level3Series: [] as number[]
      }
    );

  const series = [
    { name: '1 Star', data: level1Series },
    { name: '2 Stars', data: level2Series },
    { name: '3 Stars', data: level3Series }
  ];

  const handleChartClick = (index: number): void => {
    setQuery({ entityType: ReportEntityType.STUDENT, entityId: [...ids][index] }, 'pushIn');
  };

  const options = merge({}, BAR_CHART_COMMON_OPTIONS, {
    colors: MODULE_LEVELS_COLOR_MAP[module],
    yaxis: {
      forceNiceScale: false,
      max: MODULE_LENGTH_MAP[module],
      title: { text: 'No. of Stars' }
    },
    xaxis: { categories: [...students] },
    stroke: { show: true, colors: ['transparent'], width: 10 }
  });

  useEffect(() => {
    fetchReport({
      variables: merge(getQueryVariables({ entityId, entityType, dateRange }), {
        where: {
          module
        }
      })
    });
  }, [fetchReport, entityId, entityType, dateRange, module]);

  return (
    <ReportCard
      title={t(`Progress for ${t(module, { ns: 'ModuleType' })}`)}
      loading={loading}
      reportData={reportData}
    >
      <BarChart
        height={250}
        width={fullChartWidth}
        series={series}
        options={options}
        onClick={handleChartClick}
      />
    </ReportCard>
  );
};

export default ProgressForStudentsChartView;
